/** Map grader/snake_case monitoring payloads into existing Monitoring UI shapes. */

export const ENGINE_SERIES_META = [
	{ key: "openai", label: "GPT", color: "#4F46E5" },
	{ key: "gemini", label: "Gemini", color: "#0EA5E9" },
	{ key: "claude", label: "Claude", color: "#EA580C" },
	{ key: "perplexity", label: "Perplexity", color: "#059669" },
	{ key: "ai_overviews", label: "AI Overviews", color: "#9333EA" },
	{ key: "aiOverviews", label: "AI Overviews", color: "#9333EA" },
];

const ENGINE_LABELS = {
	openai: "GPT",
	gpt: "GPT",
	gemini: "Gemini",
	claude: "Claude",
	perplexity: "Perplexity",
	"ai-overviews": "AI Overviews",
	ai_overviews: "AI Overviews",
	aiOverviews: "AI Overviews",
};

const COLORS = ["#312E81", "#4F46E5", "#818CF8", "#A5B4FC", "#C7D2FE", "#D1D5DB"];

export function pct(value) {
	if (value == null || Number.isNaN(Number(value))) return null;
	const n = Number(value);
	// Treat 0–1 range as fraction (except exact 1 which may be 100% or score)
	if (n > 0 && n <= 1) return Math.round(n * 100);
	return Math.round(n);
}

/** Percent for overview metric cards — keep API precision (no integer rounding). */
export function pctExact(value) {
	if (value == null || Number.isNaN(Number(value))) return null;
	const n = Number(value);
	if (n > 0 && n <= 1) return n * 100;
	return n;
}

/** Score / pts deltas — keep API precision (no rounding). */
function scoreDelta(value) {
	if (value == null || Number.isNaN(Number(value))) return null;
	const n = Number(value);
	return Object.is(n, -0) ? 0 : n;
}

/**
 * Parse API date/timestamp values safely.
 * Accepts ISO timestamps (`2026-08-05T21:45:00Z`), date-only (`2026-08-05`),
 * and the invalid date-only+Z form (`2026-08-05Z`) previously returned by competitors.
 */
function parseApiDate(raw) {
	if (raw == null || raw === "") return null;
	const s = String(raw).trim();
	const dateOnlyZ = s.match(/^(\d{4}-\d{2}-\d{2})Z$/i);
	if (dateOnlyZ) {
		const d = new Date(`${dateOnlyZ[1]}T00:00:00.000Z`);
		return Number.isNaN(d.getTime()) ? null : d;
	}
	const d = new Date(s);
	return Number.isNaN(d.getTime()) ? null : d;
}

/** UTC calendar day `YYYY-MM-DD` for chart axis grouping across engines/runs. */
function toUtcDayKey(raw) {
	const d = parseApiDate(raw);
	if (!d) return String(raw || "");
	return d.toISOString().slice(0, 10);
}

export function formatShortDate(iso) {
	if (!iso) return "";
	try {
		const d = parseApiDate(iso);
		if (!d) return String(iso);
		// Use UTC so overview dates match API created_at (Z timestamps).
		return d.toLocaleDateString("en-GB", {
			timeZone: "UTC",
			month: "short",
			day: "numeric",
		});
	} catch {
		return String(iso);
	}
}

function formatDateTime(iso) {
	if (!iso) return "";
	try {
		const d = parseApiDate(iso);
		if (!d) return String(iso);
		// Use UTC so the journal matches API created_at (Z timestamps).
		return d.toLocaleString("en-GB", {
			timeZone: "UTC",
			day: "numeric",
			month: "short",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
		});
	} catch {
		return String(iso);
	}
}

export function engineLabel(key) {
	if (!key) return "";
	const k = String(key);
	return ENGINE_LABELS[k] || ENGINE_LABELS[k.toLowerCase()] || k;
}

function sourceLabel(source) {
	const s = String(source || "").toLowerCase();
	if (s.includes("baseline") && s.includes("audit")) return "Baseline audit";
	if (s.includes("audit") && !s.includes("manual")) return "Baseline audit";
	if (s.includes("suggest") || s.includes("ai_suggested") || s === "ai") {
		return "AI suggested";
	}
	if (s.includes("keyword") || s.includes("generat")) return "Generated";
	if (s.includes("manual")) return "Manual";
	if (s.includes("baseline")) return "Baseline audit";
	return source || "Unknown";
}

function sourceKey(source) {
	const s = String(source || "").toLowerCase();
	if (s.includes("baseline") && s.includes("audit")) return "baseline-audit";
	if (s.includes("audit") && !s.includes("manual")) return "baseline-audit";
	if (s.includes("suggest") || s.includes("ai_suggested") || s === "ai") {
		return "ai-suggested";
	}
	if (s.includes("keyword") || s.includes("generat")) return "generated";
	if (s.includes("manual")) return "manual";
	if (s.includes("baseline")) return "baseline-audit";
	return "manual";
}

export function intentLabel(intent) {
	if (!intent) return "";
	return String(intent)
		.replace(/_/g, " ")
		.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export function journeyLabel(stage) {
	if (!stage) return "";
	return String(stage)
		.replace(/_/g, " ")
		.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function sentimentKey(sentiment) {
	const s = String(sentiment || "").toLowerCase();
	if (s.includes("pos")) return "positive";
	if (s.includes("neg")) return "negative";
	if (!s) return "neutral";
	return "neutral";
}

function seriesByEngine(engineMap, valueKey) {
	const map = engineMap || {};
	const keys = Object.keys(map);
	const dateSet = new Map();
	keys.forEach((eng) => {
		(map[eng] || []).forEach((point) => {
			// Group by UTC day so ISO timestamps from the same run day align on one X tick.
			const key = toUtcDayKey(point.date);
			if (!dateSet.has(key)) {
				dateSet.set(key, {
					week: formatShortDate(point.date) || key,
					_sort: key,
				});
			}
			const raw = valueKey ? point[valueKey] : point.sov ?? point.value;
			dateSet.get(key)[eng] = pct(raw) ?? 0;
		});
	});
	return Array.from(dateSet.values()).sort((a, b) =>
		String(a._sort).localeCompare(String(b._sort))
	);
}

export function adaptProject(project) {
	if (!project) return null;
	return {
		hostname: project.domain || project.brand_name || "—",
		status: project.status || "active",
		orgId: project.organisation_id,
		siteId: project.website_id,
		brandName: project.brand_name,
		domain: project.domain,
		baselineAuditId: project.baseline_audit_id,
		settingsOverrides: project.settings_overrides || {},
		effectiveSettings: project.effective_settings || {},
		spendMtdUsd:
			project.spend_mtd_usd != null ? Number(project.spend_mtd_usd) : null,
		raw: project,
	};
}

export const MONITORING_ENGINE_OPTIONS = [
	"openai",
	"gemini",
	"claude",
	"perplexity",
];

export const MONITORING_CADENCE_OPTIONS = [
	{ value: "daily", label: "Daily" },
	{ value: "weekly", label: "Weekly" },
	{ value: "monthly", label: "Monthly" },
	{ value: "interval", label: "Every N days" },
];

const CADENCE_INTERVAL_MIN_DAYS = 1;
const CADENCE_INTERVAL_MAX_DAYS = 90;

/** Clamp interval days to the API-supported 1–90 range. */
export function normalizeIntervalDays(raw, fallback = 3) {
	const n = Math.round(Number(raw));
	if (!Number.isFinite(n)) return fallback;
	return Math.min(
		CADENCE_INTERVAL_MAX_DAYS,
		Math.max(CADENCE_INTERVAL_MIN_DAYS, n)
	);
}

/** Human-readable cadence for settings summaries and lists. */
export function formatCadenceLabel(cadence, intervalDays) {
	const c = String(cadence || "daily").toLowerCase();
	if (c === "daily") return "Daily";
	if (c === "weekly") return "Weekly";
	if (c === "monthly") return "Monthly";
	if (c === "interval") {
		const n = normalizeIntervalDays(intervalDays, 3);
		return `every ${n} day${n === 1 ? "" : "s"}`;
	}
	return c;
}

/** Local clock times (HH:00) for prompt / re-audit run preferences. */
export const MONITORING_RUN_TIME_OPTIONS = Array.from({ length: 24 }, (_, hour) => {
	const value = `${String(hour).padStart(2, "0")}:00`;
	return { value, label: value };
});

function normalizeRunTime(raw, fallback = "03:00") {
	const s = String(raw || "").trim();
	const match = s.match(/^(\d{1,2}):(\d{2})$/);
	if (!match) return fallback;
	const hour = Number(match[1]);
	const minute = Number(match[2]);
	if (
		!Number.isFinite(hour) ||
		!Number.isFinite(minute) ||
		hour < 0 ||
		hour > 23 ||
		minute < 0 ||
		minute > 59
	) {
		return fallback;
	}
	return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/** Format next scheduled run for the Settings upcoming banner (UTC). */
export function formatScheduleUpcoming(iso) {
	if (!iso) return null;
	try {
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return String(iso);
		return d.toLocaleString("en-GB", {
			timeZone: "UTC",
			day: "numeric",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
		});
	} catch {
		return String(iso);
	}
}

/**
 * Curated UTC-offset list (UTC-12 → UTC+13), one representative IANA zone per
 * offset. Kept in sync with frontend/src/lib/timezones.ts TIMEZONES.
 */
export const MONITORING_TIMEZONE_OPTIONS = [
	{
		value: "Etc/GMT+12",
		label:
			"UTC-12:00 — Baker Island, Howland Island (US Minor Outlying Islands)",
	},
	{ value: "Pacific/Pago_Pago", label: "UTC-11:00 — American Samoa, Niue" },
	{
		value: "Pacific/Honolulu",
		label:
			"UTC-10:00 — Hawaii-Aleutian Standard Time (US), Cook Islands",
	},
	{
		value: "Pacific/Marquesas",
		label: "UTC-09:30 — Marquesas Islands (French Polynesia)",
	},
	{
		value: "America/Anchorage",
		label: "UTC-09:00 — Alaska Standard Time (US), Gambier Islands",
	},
	{
		value: "America/Los_Angeles",
		label: "UTC-08:00 — Pacific Standard Time (US, Canada, Mexico)",
	},
	{
		value: "America/Denver",
		label: "UTC-07:00 — Mountain Standard Time (US, Canada, Mexico)",
	},
	{
		value: "America/Chicago",
		label:
			"UTC-06:00 — Central Standard Time (US, Canada, Mexico, Central America)",
	},
	{
		value: "America/New_York",
		label:
			"UTC-05:00 — Eastern Standard Time (US, Canada, Caribbean, Peru, Colombia)",
	},
	{
		value: "America/Caracas",
		label: "UTC-04:30 — Caracas Time (Venezuela)",
	},
	{
		value: "America/Halifax",
		label:
			"UTC-04:00 — Atlantic Standard Time (Canada, Brazil, Chile, Bolivia)",
	},
	{
		value: "America/St_Johns",
		label: "UTC-03:30 — Newfoundland Time (Canada)",
	},
	{
		value: "America/Sao_Paulo",
		label:
			"UTC-03:00 — Brasilia Standard Time (Brazil, Argentina, Uruguay, Greenland)",
	},
	{
		value: "America/Noronha",
		label:
			"UTC-02:00 — Fernando de Noronha Time (Brazil), South Georgia",
	},
	{
		value: "Atlantic/Azores",
		label: "UTC-01:00 — Azores Time (Portugal), Cape Verde",
	},
	{
		value: "UTC",
		label:
			"UTC±00:00 — Western European Time, Greenwich Mean Time (UK, Portugal, Iceland, Ghana)",
	},
	{
		value: "Europe/Paris",
		label:
			"UTC+01:00 — Central European Time (France, Germany, Italy, Spain, Nigeria)",
	},
	{
		value: "Europe/Athens",
		label:
			"UTC+02:00 — Eastern European Time (Egypt, Greece, Ukraine, South Africa, Israel)",
	},
	{
		value: "Europe/Moscow",
		label:
			"UTC+03:00 — Moscow Standard Time (Turkey, Saudi Arabia, Iraq, Kenya, Madagascar)",
	},
	{ value: "Asia/Tehran", label: "UTC+03:30 — Iran Standard Time (Iran)" },
	{
		value: "Asia/Dubai",
		label:
			"UTC+04:00 — Gulf Standard Time (UAE, Oman, Armenia, Azerbaijan, Russia)",
	},
	{
		value: "Asia/Kabul",
		label: "UTC+04:30 — Afghanistan Time (Afghanistan)",
	},
	{
		value: "Asia/Karachi",
		label:
			"UTC+05:00 — West Asia Standard Time (Pakistan, Uzbekistan, Turkmenistan, Maldives)",
	},
	{
		value: "Asia/Kolkata",
		label: "UTC+05:30 — Indian Standard Time (India, Sri Lanka)",
	},
	{ value: "Asia/Kathmandu", label: "UTC+05:45 — Nepal Time (Nepal)" },
	{
		value: "Asia/Dhaka",
		label: "UTC+06:00 — Bangladesh Time (Kazakhstan, Kyrgyzstan, Russia)",
	},
	{ value: "Asia/Yangon", label: "UTC+06:30 — Cocos Islands, Myanmar" },
	{
		value: "Asia/Bangkok",
		label:
			"UTC+07:00 — Indochina Time (Thailand, Vietnam, Indonesia, Russia)",
	},
	{
		value: "Asia/Shanghai",
		label:
			"UTC+08:00 — China Standard Time (China, Hong Kong, Australia, Singapore, Malaysia)",
	},
	{
		value: "Asia/Tokyo",
		label:
			"UTC+09:00 — Japan Standard Time, Korea Standard Time (Japan, South Korea)",
	},
	{
		value: "Australia/Adelaide",
		label:
			"UTC+09:30 — Australian Central Standard Time (Adelaide, Darwin)",
	},
	{
		value: "Australia/Sydney",
		label:
			"UTC+10:00 — Australian Eastern Standard Time (Sydney, Melbourne), Papua New Guinea",
	},
	{
		value: "Australia/Lord_Howe",
		label: "UTC+10:30 — Lord Howe Standard Time (Australia)",
	},
	{
		value: "Pacific/Guadalcanal",
		label: "UTC+11:00 — Solomon Islands, Vanuatu, New Caledonia",
	},
	{
		value: "Pacific/Norfolk",
		label: "UTC+11:30 — Norfolk Island (Australia)",
	},
	{
		value: "Pacific/Auckland",
		label: "UTC+12:00 — New Zealand Standard Time, Fiji",
	},
	{
		value: "Pacific/Chatham",
		label: "UTC+12:45 — Chatham Islands Time (New Zealand)",
	},
	{
		value: "Pacific/Tongatapu",
		label: "UTC+13:00 — Tonga, Samoa, Tokelau",
	},
];

function normalizeEngines(engines) {
	if (!Array.isArray(engines)) return [];
	return engines
		.map((e) => String(e || "").toLowerCase().trim())
		.filter(Boolean);
}

/**
 * Build editable Settings form state from project payload.
 * Prefers effective_settings (what the UI shows), falls back to overrides.
 */
export function adaptSettingsForm(project) {
	const effective = project?.effective_settings || {};
	const overrides = project?.settings_overrides || {};
	const src = { ...overrides, ...effective };
	const thresholds = {
		...(overrides.alert_thresholds || {}),
		...(effective.alert_thresholds || {}),
	};

	const engines = normalizeEngines(src.engines);
	const timezone = src.timezone || "UTC";
	const aliases = Array.isArray(src.brand_aliases)
		? src.brand_aliases.map((a) => String(a || "").trim()).filter(Boolean)
		: [];

	const brandName = String(
		project?.brand_name || project?.brandName || ""
	).trim();
	const brandNameSource = String(
		project?.brand_name_source || project?.brandNameSource || "detected"
	)
		.toLowerCase()
		.trim();
	const aliasSuggestions = Array.isArray(project?.alias_suggestions)
		? project.alias_suggestions
				.map((item) => {
					if (!item || typeof item !== "object") return null;
					const name = String(item.name || "").trim();
					if (!name) return null;
					return {
						name,
						source: item.source || "",
						evidence: item.evidence || "",
						mentions:
							item.mentions != null && Number.isFinite(Number(item.mentions))
								? Number(item.mentions)
								: null,
						firstSeenAt: item.first_seen_at || item.firstSeenAt || null,
					};
				})
				.filter(Boolean)
		: [];

	return {
		brandName,
		brandNameSource:
			brandNameSource === "manual" || brandNameSource === "user"
				? "manual"
				: "detected",
		aliasSuggestions,
		brandAliasesText: aliases.join(", "),
		promptCadence: src.prompt_cadence || "daily",
		promptIntervalDays: normalizeIntervalDays(src.prompt_interval_days, 3),
		auditCadence: src.audit_cadence || "weekly",
		auditIntervalDays: normalizeIntervalDays(src.audit_interval_days, 3),
		promptRunTime: normalizeRunTime(src.prompt_run_time, "03:00"),
		auditRunTime: normalizeRunTime(src.audit_run_time, "04:00"),
		nextPromptRunAt:
			project?.next_prompt_run_at || project?.nextPromptRunAt || null,
		nextReauditAt:
			project?.next_reaudit_at || project?.nextReauditAt || null,
		engines:
			engines.length > 0 ? engines : ["openai", "gemini", "claude"],
		enableWebSearch: Boolean(
			src.enable_web_search != null ? src.enable_web_search : true
		),
		timezone,
		sovDropPct:
			thresholds.sov_drop_pct != null ? Number(thresholds.sov_drop_pct) : 10,
		scoreDropPts:
			thresholds.score_drop_pts != null
				? Number(thresholds.score_drop_pts)
				: 5,
		citationLoss: Boolean(
			thresholds.citation_loss != null ? thresholds.citation_loss : true
		),
		keywordRegion: String(src.keyword_region || "us").toLowerCase(),
		keywordMinVolume:
			src.keyword_min_volume != null ? Number(src.keyword_min_volume) : 10,
		maxTopics: src.max_topics != null ? Number(src.max_topics) : 25,
		promptsPrimaryPerKeyword:
			src.prompts_primary_per_keyword != null
				? Number(src.prompts_primary_per_keyword)
				: 1,
		promptsSubquestionsPerKeyword:
			src.prompts_subquestions_per_keyword != null
				? Number(src.prompts_subquestions_per_keyword)
				: 5,
		generateBrandedPrompts: Boolean(
			src.generate_branded_prompts != null
				? src.generate_branded_prompts
				: true
		),
		keywordPromptAutocurate: Boolean(src.keyword_prompt_autocurate),
		sovPromptScope: (() => {
			const scope = String(src.sov_prompt_scope || "unbranded").toLowerCase();
			return scope === "all" ? "all" : "unbranded";
		})(),
		crawlPageMode: (() => {
			const mode = String(src.crawl_page_mode || "auto").toLowerCase();
			if (mode === "manual" || mode === "all") return mode;
			return "auto";
		})(),
		crawlManualUrls: Array.isArray(src.crawl_manual_urls)
			? src.crawl_manual_urls.map((u) => String(u || "").trim()).filter(Boolean)
			: [],
		crawlMaxPages:
			src.crawl_max_pages != null ? Number(src.crawl_max_pages) : 30,
		maxPrompts: src.max_prompts != null ? Number(src.max_prompts) : 250,
		monthlyBudgetUsd:
			src.monthly_budget_usd != null ? Number(src.monthly_budget_usd) : 100,
		keywordResearchMax:
			src.keyword_research_max != null
				? Number(src.keyword_research_max)
				: 100,
		spendMtdUsd: (() => {
			const raw =
				project?.spend_mtd_usd ??
				src.spend_mtd_usd ??
				project?.spendMtdUsd ??
				null;
			return raw != null && Number.isFinite(Number(raw)) ? Number(raw) : null;
		})(),
	};
}

function parseBrandAliases(text) {
	return String(text || "")
		.split(/[,;\n]+/)
		.map((a) => a.trim())
		.filter(Boolean)
		.slice(0, 10);
}

function validateCadenceInterval(cadence, intervalDays, label) {
	if (String(cadence || "").toLowerCase() !== "interval") return null;
	const n = Number(intervalDays);
	if (
		!Number.isFinite(n) ||
		n < CADENCE_INTERVAL_MIN_DAYS ||
		n > CADENCE_INTERVAL_MAX_DAYS
	) {
		return `${label} interval must be between ${CADENCE_INTERVAL_MIN_DAYS} and ${CADENCE_INTERVAL_MAX_DAYS} days.`;
	}
	return null;
}

/** Convert form state to API settings_overrides payload (snake_case). */
export function toSettingsOverridesPayload(form) {
	const promptCadence = form.promptCadence || "daily";
	const auditCadence = form.auditCadence || "weekly";
	const payload = {
		brand_aliases: parseBrandAliases(form.brandAliasesText),
		prompt_cadence: promptCadence,
		audit_cadence: auditCadence,
		prompt_run_time: normalizeRunTime(form.promptRunTime, "03:00"),
		audit_run_time: normalizeRunTime(form.auditRunTime, "04:00"),
		engines: normalizeEngines(form.engines),
		enable_web_search: Boolean(form.enableWebSearch),
		timezone: form.timezone || "UTC",
		alert_thresholds: {
			sov_drop_pct: Number(form.sovDropPct),
			score_drop_pts: Number(form.scoreDropPts),
			citation_loss: Boolean(form.citationLoss),
		},
		keyword_region: String(form.keywordRegion || "us")
			.toLowerCase()
			.trim(),
		keyword_min_volume: Number(form.keywordMinVolume),
		max_topics: Number(form.maxTopics),
		prompts_primary_per_keyword: Number(form.promptsPrimaryPerKeyword),
		prompts_subquestions_per_keyword: Number(
			form.promptsSubquestionsPerKeyword
		),
		generate_branded_prompts: Boolean(form.generateBrandedPrompts),
		keyword_prompt_autocurate: Boolean(form.keywordPromptAutocurate),
		sov_prompt_scope:
			String(form.sovPromptScope || "unbranded").toLowerCase() === "all"
				? "all"
				: "unbranded",
		crawl_page_mode: (() => {
			const mode = String(form.crawlPageMode || "auto").toLowerCase();
			if (mode === "manual" || mode === "all") return mode;
			return "auto";
		})(),
		crawl_manual_urls: Array.isArray(form.crawlManualUrls)
			? form.crawlManualUrls.map((u) => String(u || "").trim()).filter(Boolean)
			: [],
		crawl_max_pages: Number(form.crawlMaxPages) || 30,
		max_prompts: Number(form.maxPrompts),
		monthly_budget_usd: Number(form.monthlyBudgetUsd),
		keyword_research_max: Number(form.keywordResearchMax),
	};
	if (promptCadence === "interval") {
		payload.prompt_interval_days = normalizeIntervalDays(
			form.promptIntervalDays,
			3
		);
	}
	if (auditCadence === "interval") {
		payload.audit_interval_days = normalizeIntervalDays(
			form.auditIntervalDays,
			3
		);
	}
	return payload;
}

/** Compare crawl-scope fields to decide if a re-audit prompt is needed. */
export function crawlScopeChanged(before, after) {
	if (!before || !after) return false;
	const modeA = String(before.crawlPageMode || "auto").toLowerCase();
	const modeB = String(after.crawlPageMode || "auto").toLowerCase();
	if (modeA !== modeB) return true;
	const urlsA = Array.isArray(before.crawlManualUrls)
		? [...before.crawlManualUrls].map(String).sort()
		: [];
	const urlsB = Array.isArray(after.crawlManualUrls)
		? [...after.crawlManualUrls].map(String).sort()
		: [];
	if (urlsA.length !== urlsB.length) return true;
	return urlsA.some((u, i) => u !== urlsB[i]);
}

export function validateSettingsForm(form) {
	const brandName = String(form?.brandName || "").trim();
	if (!brandName) {
		return "Brand name is required.";
	}
	const engines = normalizeEngines(form?.engines);
	if (engines.length < 1) {
		return "Select at least one AI engine.";
	}
	if (!/^\d{2}:\d{2}$/.test(normalizeRunTime(form?.promptRunTime, ""))) {
		return "Prompt run time must be a valid HH:MM time.";
	}
	if (!/^\d{2}:\d{2}$/.test(normalizeRunTime(form?.auditRunTime, ""))) {
		return "Re-audit run time must be a valid HH:MM time.";
	}
	const promptIntervalError = validateCadenceInterval(
		form.promptCadence,
		form.promptIntervalDays,
		"Prompt"
	);
	if (promptIntervalError) return promptIntervalError;
	const auditIntervalError = validateCadenceInterval(
		form.auditCadence,
		form.auditIntervalDays,
		"Re-audit"
	);
	if (auditIntervalError) return auditIntervalError;
	const sov = Number(form.sovDropPct);
	const score = Number(form.scoreDropPts);
	if (!Number.isFinite(sov) || sov < 0) {
		return "Share-of-voice drop must be 0 or greater.";
	}
	if (!Number.isFinite(score) || score < 0) {
		return "Score drop must be 0 or greater.";
	}
	const aliases = parseBrandAliases(form.brandAliasesText);
	if (aliases.length > 10) {
		return "Brand aliases are limited to 10 names.";
	}
	const region = String(form.keywordRegion || "").trim();
	if (!region || region.length < 2) {
		return "Keyword region must be a valid ISO country code.";
	}
	const minVol = Number(form.keywordMinVolume);
	if (!Number.isFinite(minVol) || minVol < 0) {
		return "Min search volume must be 0 or greater.";
	}
	const maxTopics = Number(form.maxTopics);
	if (!Number.isFinite(maxTopics) || maxTopics < 1) {
		return "Max topics must be at least 1.";
	}
	const primary = Number(form.promptsPrimaryPerKeyword);
	if (!Number.isFinite(primary) || primary < 1 || primary > 3) {
		return "Primary prompts per keyword must be between 1 and 3.";
	}
	const subq = Number(form.promptsSubquestionsPerKeyword);
	if (!Number.isFinite(subq) || subq < 0 || subq > 10) {
		return "Sub-questions per keyword must be between 0 and 10.";
	}
	const maxPrompts = Number(form.maxPrompts);
	if (!Number.isFinite(maxPrompts) || maxPrompts < 1) {
		return "Max prompts must be at least 1.";
	}
	const budget = Number(form.monthlyBudgetUsd);
	if (!Number.isFinite(budget) || budget < 0) {
		return "Monthly budget must be 0 or greater.";
	}
	const researchMax = Number(form.keywordResearchMax);
	if (!Number.isFinite(researchMax) || researchMax < 1) {
		return "Keyword research max must be at least 1.";
	}
	const crawlMode = String(form.crawlPageMode || "auto").toLowerCase();
	if (crawlMode === "manual") {
		const urls = Array.isArray(form.crawlManualUrls) ? form.crawlManualUrls : [];
		if (urls.length < 1) {
			return "Select at least one page for manual crawl scope.";
		}
	}
	return null;
}

export function adaptOverview(overview) {
	if (!overview) return null;

	const sov = overview.share_of_voice || {};
	const cited = overview.cited_count ?? 0;
	const tested = overview.prompts_tested ?? 0;
	const citationPct =
		overview.citation_rate != null
			? pctExact(overview.citation_rate)
			: tested
				? (100 * cited) / tested
				: null;

	const aeoDelta =
		overview.latest_scores?.aeo_score != null &&
		overview.previous_scores?.aeo_score != null
			? scoreDelta(
					overview.latest_scores.aeo_score - overview.previous_scores.aeo_score
				)
			: null;
	const geoDelta =
		overview.latest_scores?.geo_score != null &&
		overview.previous_scores?.geo_score != null
			? scoreDelta(
					overview.latest_scores.geo_score - overview.previous_scores.geo_score
				)
			: null;

	const spend = overview.spend || {};
	const attention = (overview.needs_attention || []).map((evt) => ({
		type: String(evt.severity || evt.event_type || "alert")
			.toLowerCase()
			.includes("gain")
			? "journal"
			: "alert",
		message: evt.title || evt.event_type || "Attention item",
		id: evt.id,
		createdAt: evt.created_at,
		dateLabel: formatShortDate(evt.created_at) || null,
		severity: evt.severity,
		eventType: evt.event_type || null,
	}));

	const citationSparkline = (overview.citation_rate_series || []).map(
		(r) => pctExact(r.value) ?? 0
	);
	const citationRunCount =
		overview.runs_7d != null
			? Number(overview.runs_7d)
			: citationSparkline.length || null;
	const citationAvg7d =
		overview.citation_rate_7d_avg != null
			? pctExact(overview.citation_rate_7d_avg)
			: citationSparkline.length
				? citationSparkline.reduce((sum, v) => sum + v, 0) /
					citationSparkline.length
				: null;

	const sovScopeRaw = overview.sov_scope || null;
	const sovScope = sovScopeRaw
		? String(sovScopeRaw)
				.replace(/_/g, " ")
				.replace(/\b\w/g, (ch) => ch.toUpperCase())
		: "Unbranded";

	return {
		sovScopeKey:
			String(sovScopeRaw || "unbranded").toLowerCase() === "all"
				? "all"
				: "unbranded",
		sovScopeLabel: sovScope,
		metrics: {
			shareOfVoice: {
				value: pctExact(sov.value) ?? 0,
				deltaPts: sov.delta_pts != null ? Number(sov.delta_pts) : null,
				deltaLabel: "this week",
				competitorsTracked: Boolean(sov.competitors_tracked),
			},
			citationRate: {
				value: citationPct ?? 0,
				unitLabel: " of engine answers",
				cited,
				total: tested,
				windowLabel: "latest run",
				sparkline: citationSparkline,
				avg7d: citationAvg7d,
				runCount: citationRunCount,
			},
			mentionRate: {
				value: pctExact(overview.mention_rate) ?? 0,
				brandWithoutLinkPct:
					pctExact(overview.named_without_link_rate) ?? 0,
				sparkline: (overview.mention_rate_series || []).map(
					(r) => pctExact(r.value) ?? 0
				),
			},
			latestScores: {
				aeo: {
					value: overview.latest_scores?.aeo_score ?? null,
					delta: aeoDelta,
				},
				geo: {
					value: overview.latest_scores?.geo_score ?? null,
					delta: geoDelta,
				},
				reAuditLabel: overview.latest_scores?.created_at
					? `Re-audit of ${formatShortDate(overview.latest_scores.created_at)}`
					: "Latest scores",
				auditId: overview.latest_scores?.audit_id || null,
			},
		},
		sovByEngine: seriesByEngine(overview.sov_engine_series, "sov"),
		citationByEngine: seriesByEngine(overview.engine_series, "citation_rate"),
		mentionByEngine: seriesByEngine(overview.engine_series, "mention_rate"),
		engineKeys: Array.from(
			new Set([
				...Object.keys(overview.sov_engine_series || {}),
				...Object.keys(overview.engine_series || {}),
			])
		),
		spend: {
			used: Number(spend.mtd_usd ?? 0),
			cap: Number(spend.budget_usd ?? 0),
			pct: spend.used_pct != null ? Number(spend.used_pct) : 0,
			daysElapsed: null,
			projected: null,
		},
		attention,
	};
}

/** POC Change journal: newest → through latest audit's score_changed; drop older. */
function truncateChangeEventsForJournal(events, snapshots) {
	const list = Array.isArray(events) ? events : [];
	if (!list.length) return list;

	let latest = null;
	for (const snap of snapshots || []) {
		const t = Date.parse(snap?.created_at);
		if (!Number.isFinite(t)) continue;
		if (!latest || t >= Date.parse(latest.created_at)) latest = snap;
	}
	if (!latest) return list;

	const auditId = latest.audit_id != null ? String(latest.audit_id) : null;
	if (auditId) {
		let lastKeepIdx = -1;
		for (let i = 0; i < list.length; i++) {
			const evt = list[i];
			const type = String(evt.event_type || "").toLowerCase();
			const evtAudit = evt?.detail?.audit_id ?? evt?.audit_id;
			if (
				type === "score_changed" &&
				evtAudit != null &&
				String(evtAudit) === auditId
			) {
				lastKeepIdx = i;
			}
		}
		if (lastKeepIdx >= 0) return list.slice(0, lastKeepIdx + 1);
	}

	const cutoff = Date.parse(latest.created_at);
	if (!Number.isFinite(cutoff)) return list;
	return list.filter((evt) => {
		const t = Date.parse(evt.created_at);
		return !Number.isFinite(t) || t >= cutoff;
	});
}

export function adaptHistory(history) {
	if (!history) return null;

	const snapshots = history.audit_snapshots || [];
	const scoreSeries = snapshots.map((snap) => ({
		date: formatShortDate(snap.created_at) || snap.created_at,
		aeo: snap.aeo_score,
		geo: snap.geo_score,
		auditId: snap.audit_id,
	}));

	const changeEvents = truncateChangeEventsForJournal(
		history.change_events || [],
		snapshots
	).map((evt) => {
		const type = String(evt.event_type || evt.severity || "audit").toLowerCase();
		let uiType = "audit";
		if (type.includes("alert") || type.includes("lost") || type.includes("critical"))
			uiType = "alert";
		else if (type.includes("gain") || type.includes("won") || type.includes("score"))
			uiType = "gain";
		else if (type.includes("setting")) uiType = "settings";

		const detail =
			typeof evt.detail === "string"
				? evt.detail
				: evt.detail && typeof evt.detail === "object"
					? Object.values(evt.detail)
							.filter((v) => typeof v === "string")
							.join(" · ") || ""
					: "";

		return {
			id: evt.id,
			date: formatDateTime(evt.created_at) || "",
			category: String(evt.event_type || "EVENT").replace(/_/g, " ").toUpperCase(),
			type: uiType,
			severity: evt.severity,
			headline: evt.title || evt.event_type || "Change",
			detail,
			reportLink: Boolean(evt.run_id),
		};
	});

	const timeline = history.prompt_timeline || {};
	const runs = timeline.runs || [];
	const dates = runs.map(
		(r) => formatShortDate(r.date) || r.date
	);

	const prompts = (timeline.prompts || []).map((p) => {
		const cells = (p.cells || []).map((c) => {
			const s = String(c || "").toLowerCase();
			if (s === "cited" || s === "c") return "cited";
			if (s === "lost" || s === "l") return "lost";
			return "not";
		});
		const citedCount = cells.filter((c) => c === "cited").length;
		const lastLost = [...cells].reverse().findIndex((c) => c === "lost");
		let summaryKind = citedCount === 0 ? "never" : "cited";
		let summary =
			summaryKind === "never"
				? "Never cited"
				: `Cited ${citedCount}/${cells.length || 1}`;
		if (lastLost >= 0) {
			const lostIdx = cells.length - 1 - lastLost;
			summaryKind = "lost";
			summary = `Lost ${dates[lostIdx] || ""}`.trim();
		}
		const status = String(p.status || "active").toLowerCase();
		const retired =
			status === "retired" || Boolean(p.retired) || Boolean(p.is_retired);
		const personaId =
			p.persona_id != null ? Number(p.persona_id) : p.personaId != null ? Number(p.personaId) : 0;
		const personaName = p.persona_name ?? p.personaName ?? null;
		let label = p.prompt_text;
		if (personaId) {
			label = `${p.prompt_text} · ${personaName || `Persona #${personaId}`}`;
		}
		return {
			id: String(p.prompt_id),
			label,
			personaId,
			personaName,
			cells,
			summary,
			summaryKind,
			status,
			retired,
		};
	}).filter((row) => row.status === "active");

	return {
		scoreSeries,
		changeEvents,
		citationTimeline: { dates, prompts },
	};
}

function mapLatestAnalysis(latestRaw) {
	if (!latestRaw || typeof latestRaw !== "object") return null;
	return {
		analysisId:
			latestRaw.analysis_id != null ? Number(latestRaw.analysis_id) : null,
		viewAnalysis: Boolean(latestRaw.view_analysis),
	};
}

function mapPromptMetricsFields(raw) {
	const enginesCitedRaw = Array.isArray(raw.engines_cited) ? raw.engines_cited : [];
	const enginesCited = enginesCitedRaw.map(engineLabel);
	const trend = Array.isArray(raw.trend) ? raw.trend.map((v) => Number(v) || 0) : [];
	const declining =
		trend.length >= 2 &&
		trend[trend.length - 1] < trend[Math.max(0, trend.length - 3)];
	const enginesTested = Number(raw.engines_tested ?? 0) || enginesCited.length || 0;
	const citedCount = Number(raw.cited_count ?? enginesCitedRaw.length ?? 0);
	return {
		enginesCited,
		enginesCitedRaw: enginesCitedRaw.map((e) => String(e).toLowerCase()),
		citedCount,
		enginesTested,
		cited: `${citedCount}/${enginesTested || 0}`,
		avgPosition:
			raw.avg_position != null && raw.avg_position !== ""
				? Number(raw.avg_position)
				: null,
		sentiment: sentimentKey(raw.sentiment),
		trend,
		trendDeclining: declining,
		latestAnalysis: mapLatestAnalysis(raw.latest_analysis),
	};
}

export function adaptPromptVariant(v) {
	const personaId = Number(v.persona_id ?? 0);
	return {
		promptId: Number(v.prompt_id),
		personaId,
		personaName: v.persona_name ?? null,
		state: String(v.state || "active").toLowerCase(),
		runEnabled: v.run_enabled !== false,
		...mapPromptMetricsFields(v),
	};
}

/** Parent prompt row when it runs as personas (or no-persona is off). */
export function isPromptGrouped(row) {
	if (!row?.variants?.length) return false;
	const hasPersona = row.variants.some((v) => v.personaId !== 0);
	const noPersonaOff =
		String(row.status || "").toLowerCase() === "active" &&
		row.variants.some((v) => v.personaId === 0 && v.state === "retired");
	return hasPersona || noPersonaOff;
}

/**
 * Identity for retire/reactivate on a persona run row.
 * No-persona (personaId 0) uses parent-scoped SetPromptNoPersona, not RetirePrompt.
 */
export function getPromptRunLifecycleContext(row, parentPromptId = null) {
	const personaId =
		row.variantPersonaId != null
			? Number(row.variantPersonaId)
			: row.personaId != null
				? Number(row.personaId)
				: null;
	const parentId =
		parentPromptId != null
			? Number(parentPromptId)
			: row.parentPromptId != null
				? Number(row.parentPromptId)
				: null;
	return {
		variantPromptId: Number(row.promptId),
		parentPromptId: parentId,
		personaId,
	};
}

export function shouldToggleNoPersonaRun({ parentPromptId, personaId }) {
	return parentPromptId != null && personaId === 0;
}

export function variantRowFromParent(parent, variant, personaLabel) {
	const label =
		variant.personaId === 0
			? "No persona"
			: personaLabel ||
				variant.personaName ||
				(variant.personaId ? `Persona #${variant.personaId}` : "Persona");
	return {
		...parent,
		id: String(variant.promptId),
		promptId: variant.promptId,
		parentPromptId: Number(parent.promptId),
		isVariant: true,
		variantLabel: label,
		variantState: variant.state,
		variantPersonaId: variant.personaId,
		enginesCited: variant.enginesCited,
		enginesCitedRaw: variant.enginesCitedRaw,
		citedCount: variant.citedCount,
		enginesTested: variant.enginesTested,
		cited: variant.cited,
		avgPosition: variant.avgPosition,
		sentiment: variant.sentiment,
		trend: variant.trend,
		trendDeclining: variant.trendDeclining,
		latestAnalysis: variant.latestAnalysis,
		status:
			variant.state === "retired" && String(parent.status) !== "retired"
				? "retired"
				: parent.status,
	};
}

export function adaptPromptsList(api) {
	const rows = (api?.prompts || []).map((p) => {
		const intent = p.intent ? String(p.intent).toLowerCase() : null;
		const journeyStage = p.journey_stage
			? String(p.journey_stage).toLowerCase()
			: null;
		const variants = Array.isArray(p.variants)
			? p.variants.map((v) => adaptPromptVariant(v))
			: [];
		return {
			id: String(p.prompt_id),
			promptId: p.prompt_id,
			prompt: p.prompt_text || "",
			source: sourceKey(p.source),
			sourceRaw: p.source,
			sourceLabel: sourceLabel(p.source),
			...mapPromptMetricsFields(p),
			status: String(p.status || "active").toLowerCase(),
			topicId: p.topic_id != null ? Number(p.topic_id) : null,
			topic: p.topic || null,
			intent,
			intentLabel: intentLabel(intent),
			journeyStage,
			journeyLabel: journeyLabel(journeyStage),
			isBranded: Boolean(p.is_branded),
			personaIds: Array.isArray(p.persona_ids)
				? p.persona_ids.map((id) => Number(id))
				: [],
			runEnabled: p.run_enabled !== false,
			variants,
			detail: null,
		};
	});

	return {
		filters: {
			engines: [
				{ value: "", label: "All engines" },
				{ value: "openai", label: "OpenAI" },
				{ value: "gemini", label: "Gemini" },
				{ value: "claude", label: "Claude" },
				{ value: "perplexity", label: "Perplexity" },
			],
			sources: [
				{ value: "all", label: "All sources" },
				{ value: "baseline_audit", label: "Baseline audit" },
				{ value: "manual", label: "Manual" },
				{ value: "generated", label: "Generated" },
				{ value: "ai_suggested", label: "AI suggested" },
			],
			intents: [
				{ value: "all", label: "All intents" },
				{ value: "informational", label: "Informational" },
				{ value: "commercial", label: "Commercial" },
				{ value: "transactional", label: "Transactional" },
				{ value: "navigational", label: "Navigational" },
				{ value: "local", label: "Local" },
			],
			journeyStages: [
				{ value: "all", label: "All journey stages" },
				{ value: "awareness", label: "Awareness" },
				{ value: "consideration", label: "Consideration" },
				{ value: "decision", label: "Decision" },
			],
			locales: ["en-US"],
		},
		totalCount: api?.total ?? rows.length,
		maxPersonasPerPrompt: Number(api?.maxPersonasPerPrompt ?? 0),
		rows,
		webSearch: api?.webSearch,
		engineFilter: api?.engineFilter,
	};
}

export function adaptTopicsPayload(data) {
	const topics = Array.isArray(data?.topics)
		? data.topics.map((t) => ({
				topicId: Number(t.topic_id ?? t.topicId ?? t.id),
				name: String(t.name || t.topic || "Untitled topic"),
				intentPrimary: t.intent_primary
					? String(t.intent_primary).toLowerCase()
					: null,
				source: t.source || null,
				promptCount: Number(t.prompt_count ?? t.promptCount ?? 0),
				keywordCount: Number(t.keyword_count ?? t.keywordCount ?? 0),
				citedPrompts: Number(t.cited_prompts ?? t.citedPrompts ?? 0),
				testedPrompts: Number(t.tested_prompts ?? t.testedPrompts ?? 0),
				createdAt: t.created_at || t.createdAt || null,
			}))
		: [];

	topics.sort((a, b) => a.name.localeCompare(b.name));

	const unassignedRaw = data?.unassigned || {};
	const unassigned = {
		promptCount: Number(unassignedRaw.prompt_count ?? unassignedRaw.promptCount ?? 0),
		keywordCount: Number(
			unassignedRaw.keyword_count ?? unassignedRaw.keywordCount ?? 0
		),
		citedPrompts: Number(
			unassignedRaw.cited_prompts ?? unassignedRaw.citedPrompts ?? 0
		),
		testedPrompts: Number(
			unassignedRaw.tested_prompts ?? unassignedRaw.testedPrompts ?? 0
		),
	};

	return { topics, unassigned };
}

/**
 * Group adapted prompt rows under topic metadata. Unassigned always last.
 */
export function buildPromptTopicGroups(rows, topicsPayload) {
	const topics = topicsPayload?.topics || [];
	const byId = new Map(topics.map((t) => [t.topicId, { ...t, prompts: [] }]));
	const orphanTopics = new Map();
	const unassignedPrompts = [];

	(rows || []).forEach((row) => {
		if (row.topicId == null) {
			unassignedPrompts.push(row);
			return;
		}
		if (byId.has(row.topicId)) {
			byId.get(row.topicId).prompts.push(row);
			return;
		}
		if (!orphanTopics.has(row.topicId)) {
			orphanTopics.set(row.topicId, {
				topicId: row.topicId,
				name: row.topic || `Topic ${row.topicId}`,
				intentPrimary: row.intent || null,
				source: null,
				promptCount: 0,
				keywordCount: 0,
				citedPrompts: 0,
				testedPrompts: 0,
				createdAt: null,
				prompts: [],
			});
		}
		orphanTopics.get(row.topicId).prompts.push(row);
	});

	const groups = [...byId.values(), ...orphanTopics.values()]
		.map((g) => {
			const promptCount = g.prompts.length || g.promptCount;
			const citedFromRows = g.prompts.filter((p) => p.citedCount > 0).length;
			return {
				...g,
				promptCount,
				citedPrompts: g.prompts.length ? citedFromRows : g.citedPrompts,
				testedPrompts: g.prompts.length ? g.prompts.length : g.testedPrompts,
			};
		})
		.filter((g) => g.prompts.length > 0)
		.sort((a, b) => {
			const byCount = (b.promptCount || 0) - (a.promptCount || 0);
			if (byCount !== 0) return byCount;
			return String(a.name || "").localeCompare(String(b.name || ""));
		});

	const unassignedMeta = topicsPayload?.unassigned || {
		promptCount: 0,
		keywordCount: 0,
		citedPrompts: 0,
		testedPrompts: 0,
	};

	return {
		groups,
		unassigned: {
			...unassignedMeta,
			promptCount: unassignedPrompts.length || unassignedMeta.promptCount,
			prompts: unassignedPrompts,
		},
	};
}

/**
 * Group adapted keywords under topic metadata (same shape as prompts topics).
 */
export function buildKeywordTopicGroups(keywords, topicsPayload) {
	const topics = topicsPayload?.topics || [];
	const byId = new Map(topics.map((t) => [t.topicId, { ...t, keywords: [] }]));
	const orphanTopics = new Map();
	const unassignedKeywords = [];

	(keywords || []).forEach((row) => {
		if (row.topicId == null || Number.isNaN(Number(row.topicId))) {
			unassignedKeywords.push(row);
			return;
		}
		const tid = Number(row.topicId);
		if (byId.has(tid)) {
			byId.get(tid).keywords.push(row);
			return;
		}
		if (!orphanTopics.has(tid)) {
			orphanTopics.set(tid, {
				topicId: tid,
				name: row.topic || `Topic ${tid}`,
				intentPrimary: row.intent || null,
				source: null,
				promptCount: 0,
				keywordCount: 0,
				citedPrompts: 0,
				testedPrompts: 0,
				createdAt: null,
				keywords: [],
			});
		}
		orphanTopics.get(tid).keywords.push(row);
	});

	const enrich = (g) => {
		// POC: keywords within a topic by search volume desc.
		const kws = [...(g.keywords || [])].sort(
			(a, b) => (Number(b.searchVolume) || 0) - (Number(a.searchVolume) || 0)
		);
		const volumeTotal = kws.reduce((sum, k) => sum + (Number(k.searchVolume) || 0), 0);
		// POC topic order: sum of volume for keywords mapped to tracked prompts.
		const mappedVolume = kws.reduce(
			(sum, k) =>
				Number(k.mappedPrompts) > 0
					? sum + (Number(k.searchVolume) || 0)
					: sum,
			0
		);
		const promptCountFromKws = kws.reduce(
			(sum, k) => sum + (Number(k.mappedPrompts) || 0),
			0
		);
		const cited = kws.reduce((sum, k) => {
			const c = k.citedOnMapped;
			return sum + (c?.cited != null ? Number(c.cited) : 0);
		}, 0);
		const citedOf = kws.reduce((sum, k) => {
			const c = k.citedOnMapped;
			return sum + (c?.of != null ? Number(c.of) : Number(k.mappedPrompts) || 0);
		}, 0);
		const intentCounts = {};
		kws.forEach((k) => {
			if (!k.intent) return;
			intentCounts[k.intent] = (intentCounts[k.intent] || 0) + 1;
		});
		const intentPrimary =
			g.intentPrimary ||
			Object.entries(intentCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ||
			null;

		return {
			...g,
			keywords: kws,
			intentPrimary,
			keywordCount: kws.length || g.keywordCount,
			promptCount: promptCountFromKws || g.promptCount,
			volumeTotal,
			mappedVolume,
			citedPrompts: cited || g.citedPrompts,
			testedPrompts: citedOf || g.testedPrompts || promptCountFromKws,
		};
	};

	const groups = [...byId.values(), ...orphanTopics.values()]
		.map(enrich)
		.filter((g) => (g.keywords || []).length > 0)
		.sort((a, b) => {
			const byMappedVol = (b.mappedVolume || 0) - (a.mappedVolume || 0);
			if (byMappedVol !== 0) return byMappedVol;
			return String(a.name || "").localeCompare(String(b.name || ""));
		});

	const unassignedMeta = topicsPayload?.unassigned || {
		promptCount: 0,
		keywordCount: 0,
		citedPrompts: 0,
		testedPrompts: 0,
	};

	return {
		groups,
		unassigned: enrich({
			topicId: null,
			name: "Unassigned",
			intentPrimary: null,
			...unassignedMeta,
			keywords: unassignedKeywords,
		}),
	};
}

export function adaptPromptDetail(detail) {
	if (!detail) return null;
	const series = (detail.runs || []).map((r) => ({
		date: formatShortDate(r.date) || r.date,
		count: r.cited_count ?? (r.engines_cited || []).length,
	}));
	const lost = (detail.engine_history || []).find((e) => e.lost_date);
	const narrativeParts = [];
	(detail.engine_history || []).forEach((e) => {
		const label = engineLabel(e.engine);
		if (e.first_cited_date) {
			narrativeParts.push(`First cited ${formatShortDate(e.first_cited_date)} (${label}).`);
		}
		if (e.lost_date) {
			narrativeParts.push(`${label} citation lost ${formatShortDate(e.lost_date)}.`);
		}
	});
	if (detail.latest_own_url) {
		narrativeParts.push(`Latest own URL: ${detail.latest_own_url}.`);
	}
	const lossPhrase = lost ? `lost ${formatShortDate(lost.lost_date)}` : null;
	const latestRunAt =
		detail.latest_run_at ??
		detail.last_run_at ??
		detail.latest_run_date ??
		detail.latestRunAt ??
		detail.lastRunAt ??
		null;
	const runs = Array.isArray(detail.runs) ? detail.runs : [];
	const latestRunFromSeries =
		runs.length > 0 ? runs[runs.length - 1]?.date ?? null : null;

	return {
		series,
		narrative: narrativeParts.join(" ") || "No citation history in this window.",
		lossPhrase,
		latestAnswers: detail.latest_answers || [],
		engineHistory: detail.engine_history || [],
		latestRunAt: latestRunAt || latestRunFromSeries || null,
	};
}

/** Resolve a displayable latest-run date from raw PromptDetail payload. */
export function getPromptLatestRunLabel(detail) {
	if (!detail) return "";
	const answers = detail.latest_answers || detail.latestAnswers || [];
	const fromAnswer = answers.find(
		(a) =>
			a?.run_at ||
			a?.runAt ||
			a?.run_date ||
			a?.runDate ||
			a?.answered_at ||
			a?.date ||
			a?.created_at
	);
	const iso =
		detail.latest_run_at ??
		detail.last_run_at ??
		detail.latest_run_date ??
		detail.latestRunAt ??
		detail.lastRunAt ??
		(Array.isArray(detail.runs) && detail.runs.length
			? detail.runs[detail.runs.length - 1]?.date
			: null) ??
		fromAnswer?.run_at ??
		fromAnswer?.runAt ??
		fromAnswer?.run_date ??
		fromAnswer?.runDate ??
		fromAnswer?.answered_at ??
		fromAnswer?.date ??
		fromAnswer?.created_at ??
		null;
	const label = formatShortDate(iso);
	return label || "";
}

export function adaptCompetitors(data, ourDomain = "us") {
	if (!data) return null;

	const trend = data.sov_trend || [];
	const pie = data.pie;
	const comps = data.competitors || [];

	const legend = [{ key: "us", label: `${ourDomain} (us)`, color: COLORS[0] }];
	comps.slice(0, 4).forEach((c, i) => {
		legend.push({
			key: `c${c.competitor_id}`,
			label: c.domain || c.name || `c${c.competitor_id}`,
			color: COLORS[(i + 1) % COLORS.length],
		});
	});

	const sovSeries = trend
		.map((point, idx) => {
			const parsed = parseApiDate(point.date);
			const row = {
				week:
					formatShortDate(point.date) ||
					toUtcDayKey(point.date) ||
					`W${idx + 1}`,
				_sort: parsed ? parsed.getTime() : idx,
				us: pct(point.overall_sov) ?? 0,
			};
			comps.slice(0, 4).forEach((c) => {
				row[`c${c.competitor_id}`] = 0;
			});
			return row;
		})
		.sort((a, b) => a._sort - b._sort);

	if (sovSeries.length && pie?.slices?.length) {
		const last = sovSeries[sovSeries.length - 1];
		pie.slices.forEach((slice) => {
			const key = String(slice.key || "").toLowerCase();
			if (key === "us" || key === "ours" || key === "brand" || key === "you") {
				last.us = pct(slice.share) ?? last.us;
			} else {
				const match = comps.find(
					(c) =>
						c.domain === slice.domain ||
						c.name === slice.label ||
						String(c.competitor_id) === String(slice.key)
				);
				if (match) last[`c${match.competitor_id}`] = pct(slice.share) ?? 0;
			}
		});
	}

	const citationSources = (data.top_sources || []).map((s) => ({
		domain: s.domain,
		citations30d: s.prompts ?? 0,
		overlapPct: s.overlap != null ? pct(s.overlap) : null,
	}));

	const wl = data.win_loss;
	const matrix = {
		rows: [],
		competitors: [],
		cells: {},
		footnote: null,
	};

	if (wl) {
		matrix.competitors = (wl.competitors || []).map((c) => c.domain || c.name);
		matrix.rows = (wl.rows || []).map((r) => ({
			cluster: r.cluster,
			prompts: Number(r.prompts ?? 0) || 0,
		}));
		matrix.footnote =
			wl.manual_count != null || wl.discovered_count != null
				? `${wl.manual_count ?? 0} manual · ${wl.discovered_count ?? 0} discovered competitors`
				: null;
		(wl.rows || []).forEach((row) => {
			matrix.cells[row.cluster] = {};
			(row.cells || []).forEach((cell) => {
				const comp = (wl.competitors || []).find(
					(c) => c.competitor_id === cell.competitor_id
				);
				const name = comp?.domain || comp?.name;
				if (!name) return;
				const us = Number(cell.we ?? 0) || 0;
				const them = Number(cell.they ?? 0) || 0;
				const tied = Number(cell.tied ?? 0) || 0;
				let status = "tied";
				if (us === 0 && them === 0 && tied === 0) {
					status = "empty";
				} else if (us > them) {
					status = "lead";
				} else if (them > us) {
					status = "behind";
				}
				matrix.cells[row.cluster][name] = { status, us, them, tied };
			});
		});
	}

	const mc = data.market_competition;
	const sovScope = data.sov_scope || null;
	const scopeKey = String(
		sovScope?.effective || sovScope?.scope || mc?.scope_effective || mc?.scope || "unbranded"
	).toLowerCase();
	const scopeIsAll = scopeKey === "all";
	const unbrandedPrompts = Number(sovScope?.unbranded_prompts ?? mc?.total_prompts ?? 0) || 0;
	const brandedPrompts = Number(sovScope?.branded_prompts ?? 0) || 0;
	const monitoredPrompts = unbrandedPrompts + brandedPrompts;

	return {
		sovSeries,
		sovLegend: legend,
		citationSources,
		winLossMatrix: matrix,
		competitorsList: comps,
		marketCompetition: mc
			? {
					overallSov: Number(mc.overall_sov ?? 0),
					brandMentions: mc.brand_mentions ?? 0,
					competitorMentions: mc.competitor_mentions ?? 0,
					winningPrompts: mc.winning_prompts ?? 0,
					losingPrompts: mc.losing_prompts ?? 0,
					totalPrompts: mc.total_prompts ?? 0,
					dataset: mc.dataset,
					isMeasured: Boolean(mc.is_measured),
					scopeKey: scopeIsAll ? "all" : "unbranded",
					scopeLabel: scopeIsAll ? "All prompts" : "Unbranded",
					unbrandedPrompts,
					brandedPrompts,
					monitoredPrompts,
					perProvider: Object.entries(mc.per_provider || {}).map(([engine, v]) => ({
						engine,
						label:
							String(engine).toLowerCase() === "openai"
								? "ChatGPT"
								: engineLabel(engine),
						sov: Number(v.sov ?? 0),
						brandMentions: v.brand_mentions ?? 0,
						competitorMentions: v.competitor_mentions ?? 0,
					})),
				}
			: null,
		pie: pie
			? {
					totalMentions: pie.total_mentions,
					slices: (pie.slices || []).map((s) => ({
						key: s.key,
						label: s.label,
						mentions: s.mentions,
						share: pct(s.share) ?? 0,
					})),
				}
			: null,
		raw: data,
	};
}

const RULE_LABELS = {
	sov_drop: "Share of voice / citation rate drops beyond threshold",
	score_drop: "AEO or GEO score drops beyond threshold",
	citation_loss: "Any citation lost on a tracked prompt",
	competitor_overtake: "Competitor overtakes our citation share",
	budget: "Monthly budget reached",
};

function parseAlertPayload(raw) {
	if (raw == null) return null;
	if (typeof raw === "object") return raw;
	if (typeof raw !== "string") return null;

	const trimmed = raw.trim();
	if (!trimmed) return null;

	try {
		return JSON.parse(trimmed);
	} catch {
		// Backend may serialize Python dicts with single quotes.
		try {
			const normalized = trimmed
				.replace(/'/g, '"')
				.replace(/\bNone\b/g, "null")
				.replace(/\bTrue\b/g, "true")
				.replace(/\bFalse\b/g, "false");
			return JSON.parse(normalized);
		} catch {
			return null;
		}
	}
}

function formatAlertRate(value) {
	const n = Number(value);
	if (!Number.isFinite(n)) return String(value ?? "");
	if (n >= 0 && n <= 1) return `${(n * 100).toFixed(1)}%`;
	return `${n.toFixed(1)}%`;
}

function buildAlertDetail(alert) {
	const payload =
		parseAlertPayload(alert?.body) ??
		parseAlertPayload(alert?.payload) ??
		parseAlertPayload(alert?.details) ??
		parseAlertPayload(alert?.detail) ??
		parseAlertPayload(alert?.meta) ??
		(typeof alert?.body === "object" ? alert.body : null) ??
		(typeof alert?.payload === "object" ? alert.payload : null);

	if (!payload || typeof payload !== "object") {
		const text =
			typeof alert?.body === "string"
				? alert.body.trim()
				: typeof alert?.payload === "string"
					? alert.payload.trim()
					: "";
		return text ? { kind: "text", text } : null;
	}

	const ruleKey = String(alert?.rule_key || "").toLowerCase();

	if (ruleKey === "citation_loss" && Array.isArray(payload.prompts)) {
		const prompts = payload.prompts
			.map((p) => ({
				id: p?.prompt_id ?? p?.promptId ?? p?.id ?? null,
				text: String(p?.text ?? p?.prompt_text ?? p?.promptText ?? "").trim(),
			}))
			.filter((p) => p.id != null || p.text);
		if (prompts.length) return { kind: "prompts", prompts };
	}

	if (
		(ruleKey === "sov_drop" || ruleKey === "score_drop") &&
		payload.previous != null &&
		payload.current != null
	) {
		const prev = Number(payload.previous);
		const curr = Number(payload.current);
		const delta = Number.isFinite(prev) && Number.isFinite(curr) ? curr - prev : null;
		const labelPrefix = ruleKey === "score_drop" ? "score" : "rate";
		return {
			kind: "metrics",
			items: [
				{
					label: `Previous ${labelPrefix}`,
					value: ruleKey === "score_drop" ? String(payload.previous) : formatAlertRate(prev),
				},
				{
					label: `Current ${labelPrefix}`,
					value: ruleKey === "score_drop" ? String(payload.current) : formatAlertRate(curr),
				},
				...(delta != null
					? [
							{
								label: "Change",
								value:
									ruleKey === "score_drop"
										? `${delta >= 0 ? "+" : ""}${delta.toFixed(2)}`
										: `${delta >= 0 ? "+" : ""}${(delta * 100).toFixed(1)} pts`,
							},
						]
					: []),
			],
		};
	}

	const items = Object.entries(payload)
		.filter(([, value]) => value != null && value !== "")
		.map(([key, value]) => ({
			label: key.replace(/_/g, " "),
			value:
				typeof value === "object"
					? JSON.stringify(value)
					: String(value),
		}));

	if (!items.length) return null;
	return { kind: "kv", items };
}

export function adaptAlerts({ alerts = [], rules = [] } = {}) {
	return {
		feed: alerts.map((a) => ({
			id: a.alert_id,
			severity: String(a.severity || "warning").toLowerCase(),
			title: a.title,
			detail: buildAlertDetail(a),
			timestamp: formatDateTime(a.created_at) || a.created_at || "",
			status:
				a.status === "acknowledged" || a.acknowledged_at ? "resolved" : "open",
		})),
		rules: rules.map((r) => {
			const configEntries =
				r.config && typeof r.config === "object"
					? Object.entries(r.config).filter(([, v]) => v != null)
					: [];
			const meta =
				configEntries.length > 0
					? configEntries
							.map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
							.join(" · ")
					: "Global default thresholds";
			return {
				id: r.rule_key,
				label: RULE_LABELS[r.rule_key] || (r.rule_key || "Rule").replace(/_/g, " "),
				meta: `${meta} · Channel delivery (email/Slack) ships in Phase 4`,
				enabled: Boolean(r.enabled),
			};
		}),
	};
}

function adaptCitedOnMapped(raw) {
	if (raw == null) return null;
	if (typeof raw === "object") {
		const cited = raw.cited ?? raw.Cited ?? null;
		const of = raw.of ?? raw.Of ?? raw.total ?? null;
		if (cited == null && of == null) return null;
		return {
			cited: cited != null ? Number(cited) : 0,
			of: of != null ? Number(of) : 0,
		};
	}
	const s = String(raw);
	const m = s.match(/^(\d+)\s*\/\s*(\d+)$/);
	if (m) return { cited: Number(m[1]), of: Number(m[2]) };
	return null;
}

export function adaptKeywordEmbeddedPrompt(raw = {}) {
	const citedRaw = raw.cited ?? raw.is_cited ?? raw.isCited;
	const testedRaw = raw.tested ?? raw.is_tested ?? raw.isTested;
	return {
		promptId: Number(raw.prompt_id ?? raw.promptId ?? raw.id),
		promptText: String(raw.prompt_text ?? raw.promptText ?? ""),
		isBranded:
			raw.is_branded != null
				? Boolean(raw.is_branded)
				: raw.isBranded != null
					? Boolean(raw.isBranded)
					: null,
		cited: citedRaw === true || citedRaw === 1 || citedRaw === "1",
		tested: testedRaw === true || testedRaw === 1 || testedRaw === "1",
	};
}

function normalizeAiUsage(raw) {
	if (raw == null || raw === "") return null;
	const value = String(raw).toLowerCase().trim();
	if (!value) return null;
	return value;
}

export function aiUsageLabel(value) {
	const key = normalizeAiUsage(value);
	if (!key) return null;
	if (key === "high") return "High";
	if (key === "medium") return "Medium";
	if (key === "low") return "Low";
	return key.charAt(0).toUpperCase() + key.slice(1);
}

export function adaptMonitoringKeyword(raw = {}) {
	const keywordId = raw.keyword_id ?? raw.keywordId ?? raw.id ?? null;
	const mappedPrompts = Number(
		raw.mapped_prompts ?? raw.mappedPrompts ?? raw.mapped_prompt_count ?? 0
	);
	const journeyRaw = raw.journey_stage ?? raw.journeyStage ?? null;
	const searchVolumeRaw =
		raw.search_volume ?? raw.searchVolume ?? raw.volume ?? null;
	const searchVolume =
		searchVolumeRaw != null && Number.isFinite(Number(searchVolumeRaw))
			? Number(searchVolumeRaw)
			: null;
	const aiVolumeRaw = raw.ai_search_volume ?? raw.aiSearchVolume ?? null;
	const aiVolume =
		aiVolumeRaw != null && Number.isFinite(Number(aiVolumeRaw))
			? Number(aiVolumeRaw)
			: null;
	const aiUsageRatioRaw = raw.ai_usage_ratio ?? raw.aiUsageRatio ?? null;
	const aiUsageRatio =
		aiUsageRatioRaw != null && Number.isFinite(Number(aiUsageRatioRaw))
			? Number(aiUsageRatioRaw)
			: null;
	return {
		keywordId: keywordId != null ? Number(keywordId) : null,
		keyword: String(raw.keyword || ""),
		searchVolume,
		aiVolume,
		aiUsage: normalizeAiUsage(raw.ai_usage_label ?? raw.aiUsageLabel),
		aiUsageRatio,
		keywordDifficulty: Number(
			raw.keyword_difficulty ?? raw.keywordDifficulty ?? raw.difficulty ?? 0
		),
		intent: String(raw.intent || "").toLowerCase() || null,
		journeyStage: journeyRaw
			? String(journeyRaw).toLowerCase().replace(/\s+/g, "_")
			: null,
		topicId:
			raw.topic_id != null || raw.topicId != null
				? Number(raw.topic_id ?? raw.topicId)
				: null,
		topic: raw.topic ?? raw.topic_name ?? null,
		mappedPrompts: Number.isNaN(mappedPrompts) ? 0 : mappedPrompts,
		citedOnMapped: adaptCitedOnMapped(raw.cited_on_mapped ?? raw.citedOnMapped),
		isActive: Boolean(raw.is_active ?? raw.isActive),
		source: raw.source || null,
		cpc: raw.cpc ?? null,
		region: raw.region || null,
		prompts: Array.isArray(raw.prompts)
			? raw.prompts.map(adaptKeywordEmbeddedPrompt)
			: [],
	};
}

export function adaptKeywordGroup(raw = {}) {
	const keywords = Array.isArray(raw.keywords)
		? raw.keywords.map(adaptMonitoringKeyword)
		: [];
	return {
		key: String(raw.key ?? raw.id ?? raw.label ?? ""),
		label: String(raw.label ?? raw.name ?? raw.topic ?? "Group"),
		kind: String(raw.kind || "topic").toLowerCase(),
		keywords,
	};
}

function synthesizeKeywordGroups(keywords) {
	const byKey = new Map();
	keywords.forEach((k) => {
		const hasTopic = k.topicId != null || (k.topic && String(k.topic).trim());
		const key = hasTopic
			? `t:${k.topicId ?? k.topic}`
			: `i:${k.intent || "uncategorized"}`;
		const label = hasTopic
			? String(k.topic || "Topic")
			: k.intent
				? String(k.intent)
				: "Uncategorized";
		const kind = hasTopic ? "topic" : "intent";
		if (!byKey.has(key)) {
			byKey.set(key, { key, label, kind, keywords: [] });
		}
		byKey.get(key).keywords.push(k);
	});
	return Array.from(byKey.values());
}

export function adaptKeywordsPayload(data = {}) {
	const keywords = Array.isArray(data.keywords)
		? data.keywords.map(adaptMonitoringKeyword)
		: [];
	let groups = Array.isArray(data.groups)
		? data.groups.map(adaptKeywordGroup)
		: [];
	if (!groups.length && keywords.length) {
		groups = synthesizeKeywordGroups(keywords);
	}
	const topics = Array.isArray(data.topics)
		? data.topics.map((t) => ({
				id: t.id ?? t.topic_id ?? null,
				name: t.name ?? t.topic ?? "",
				category: t.category || null,
				keywordCount: Number(t.keyword_count ?? t.keywordCount ?? 0),
				activeKeywordCount: Number(
					t.active_keyword_count ?? t.activeKeywordCount ?? 0
				),
				promptCount: Number(t.prompt_count ?? t.promptCount ?? 0),
			}))
		: [];
	const total =
		data.total != null
			? Number(data.total)
			: keywords.length;
	const active =
		data.active != null
			? Number(data.active)
			: data.active_count != null
				? Number(data.active_count)
				: keywords.filter((k) => k.isActive).length;
	const mappedCount = keywords.filter((k) => k.mappedPrompts > 0).length;
	return {
		keywords,
		groups,
		topics,
		total,
		active,
		mappedCount,
		cacheFresh: data.cacheFresh ?? data.cache_fresh ?? null,
	};
}

export function adaptCandidatePrompt(raw = {}) {
	const originRaw =
		raw.origin_keyword_id ??
		raw.originKeywordId ??
		raw.keyword_id ??
		raw.keywordId;
	return {
		promptId: Number(raw.prompt_id ?? raw.promptId ?? raw.id),
		promptText: String(raw.prompt_text ?? raw.promptText ?? raw.prompt ?? ""),
		promptType: raw.prompt_type ?? raw.promptType ?? null,
		isBranded: Boolean(raw.is_branded ?? raw.isBranded),
		intent: raw.intent ? String(raw.intent).toLowerCase() : null,
		journeyStage: raw.journey_stage ?? raw.journeyStage ?? null,
		keywordId: raw.keyword_id ?? raw.keywordId ?? null,
		originKeywordId:
			originRaw != null && originRaw !== "" ? Number(originRaw) : null,
		keyword: raw.keyword || null,
		status: raw.status || null,
		source: raw.source || null,
	};
}

export function adaptGenerationContext(raw = {}) {
	const ctx = raw.site_context ?? raw.siteContext ?? {};
	return {
		auditId: raw.audit_id ?? raw.auditId ?? null,
		businessModel: raw.business_model ?? raw.businessModel ?? "",
		websiteType: raw.website_type ?? raw.websiteType ?? "",
		siteContext: {
			pageTitles: ctx.page_titles ?? ctx.pageTitles ?? [],
			h1Tags: ctx.h1_tags ?? ctx.h1Tags ?? [],
			productNames: ctx.product_names ?? ctx.productNames ?? [],
			navLinks: ctx.nav_links ?? ctx.navLinks ?? [],
			bodySample: ctx.body_sample ?? ctx.bodySample ?? "",
		},
	};
}

export function adaptCandidateGroup(raw = {}) {
	const prompts = Array.isArray(raw.prompts)
		? raw.prompts.map(adaptCandidatePrompt)
		: [];
	return {
		topicId: raw.topic_id ?? raw.topicId ?? null,
		topic: String(raw.topic ?? raw.name ?? "Topic"),
		category: raw.category || null,
		prompts,
	};
}

export function adaptKeywordCandidatesPayload(data = {}) {
	let groups = [];
	if (Array.isArray(data.groups)) {
		groups = data.groups.map(adaptCandidateGroup);
	} else if (Array.isArray(data.categories)) {
		// Legacy doc shape: categories → topics → prompts
		data.categories.forEach((cat) => {
			(cat.topics || []).forEach((topic) => {
				groups.push(
					adaptCandidateGroup({
						topic_id: topic.id,
						topic: topic.name,
						category: cat.name,
						prompts: topic.prompts,
					})
				);
			});
		});
	}
	return {
		groups,
		total: data.total != null ? Number(data.total) : groups.reduce((n, g) => n + g.prompts.length, 0),
		status: data.status_filter || data.status || null,
	};
}

/** Deployed / mapped prompts for a single keyword (KeywordPrompts API). */
export function adaptDeployedKeywordPrompt(raw = {}) {
	const citedRaw = raw.cited ?? raw.is_cited ?? raw.isCited;
	const mentionedRaw = raw.mentioned ?? raw.is_mentioned ?? raw.isMentioned;
	return {
		promptId: Number(raw.prompt_id ?? raw.promptId ?? raw.id),
		promptText: String(raw.prompt_text ?? raw.promptText ?? raw.prompt ?? ""),
		status: raw.status || null,
		cited: citedRaw === true || citedRaw === 1 || citedRaw === "1",
		mentioned:
			mentionedRaw === true || mentionedRaw === 1 || mentionedRaw === "1",
		position: raw.position != null ? Number(raw.position) : null,
		engines: Array.isArray(raw.engines) ? raw.engines : [],
		lastRunAt: raw.last_run_at ?? raw.lastRunAt ?? null,
		promptType: raw.prompt_type ?? raw.promptType ?? null,
		intent: raw.intent ? String(raw.intent).toLowerCase() : null,
		journeyStage: raw.journey_stage ?? raw.journeyStage ?? null,
		isBranded: Boolean(raw.is_branded ?? raw.isBranded),
	};
}

export function adaptKeywordPromptsPayload(data = {}) {
	const prompts = Array.isArray(data.prompts)
		? data.prompts.map(adaptDeployedKeywordPrompt)
		: [];
	return {
		keywordId: data.keyword_id ?? data.keywordId ?? null,
		keyword: data.keyword ? String(data.keyword) : null,
		prompts,
		total: data.total != null ? Number(data.total) : prompts.length,
	};
}
